import React, { createRef, forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import VideoMeetingTileNameplate from "@platform/features/videoMeeting/components/VideoMeetingTile/VideoMeetingTileNameplate";
import type { RoomMember } from "@core/features/videoMeeting";
import useContainerWidthBreakpoint from "@core/hooks/useContainerWidthBreakpoint";

interface VideoTileProps extends React.HTMLAttributes<HTMLDivElement> {
  tag?: React.ElementType;
  nameplate?: string;
  roomMemberData?: RoomMember | null;
  onPlaying?: () => void;
}

export const PreviewVideoTile = forwardRef<HTMLVideoElement, VideoTileProps>(
  (
    { tag: Component = "div", className, nameplate, roomMemberData, onPlaying, ...rest },
    ref
  ) => {
    const containerRef = createRef<HTMLDivElement>();

    const { isXs, containerWidth } = useContainerWidthBreakpoint(containerRef);

    return (
      <Component
        className={twMerge(
          "bg-brand-100 h-full w-full relative rounded-md overflow-hidden aspect-video",
          className
        )}
        data-testid="preview-video-tile"
        ref={containerRef}
        {...rest}
      >
        <video
          ref={ref}
          className="w-full h-full object-cover transition-opacity duration-300"
          style={{ opacity: 0 }}
          playsInline
          muted
          autoPlay
          onPlaying={onPlaying}
        />
        {nameplate && (
          <VideoMeetingTileNameplate
            nameplate={nameplate}
            isWithContentShare={false}
            isWithVideo={true}
            roomMemberData={roomMemberData}
            isXs={isXs}
            containerWidth={containerWidth}
          />
        )}
      </Component>
    );
  }
);

PreviewVideoTile.displayName = "PreviewVideoTile";

export default PreviewVideoTile;
